import { useTranslation } from 'react-i18next'

import { CsvColumn, ImportStatus } from '../useCsvModal'
import { FailedImportRow, FailedReason } from '../utils'
import { Icon } from '@/Atomic/Atoms/Icon'
import {
  WizardModalProgressBar,
  WizardModalProgressState,
} from '@/Atomic/Molecules/WizardModal'

interface ImportStepProps {
  importStatus: ImportStatus
  createdCount: number
  updatedCount: number
  failedCount: number
  totalToImport: number
  importDuration: number | null
  failedImportRowValues: FailedImportRow[]
  csvColumns: CsvColumn[]
  columnMappings: Record<number, string>
}

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

export const ImportStep: React.FC<ImportStepProps> = ({
  importStatus,
  createdCount,
  updatedCount,
  failedCount,
  totalToImport,
  importDuration,
  failedImportRowValues,
  csvColumns,
  columnMappings,
}) => {
  const { t } = useTranslation()

  const reasonText = (reason: FailedReason): string => {
    switch (reason.code) {
      case 'ambiguousEmail':
        return t('components.csv.import.failedReason.ambiguousEmail', {
          count: reason.count,
        })
      case 'recordChanged':
        return t('components.csv.import.failedReason.recordChanged')
      default:
        return t('components.csv.import.failedReason.unknown')
    }
  }

  const downloadFailedRows = () => {
    const mappedCols = csvColumns.filter((col) => columnMappings[col.rawIndex])
    const headers = [
      ...mappedCols.map((col) => col.header),
      t('components.csv.analysis.error'),
    ].join(',')
    const rows = failedImportRowValues
      .map((failed) =>
        [
          ...mappedCols.map((col) => failed.values[col.rawIndex] ?? ''),
          reasonText(failed.reason),
        ].join(','),
      )
      .join('\n')
    const blob = new Blob([`${headers}\n${rows}`], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'failed_imports.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  if (importStatus === 'running') {
    const processedCount = createdCount + updatedCount + failedCount
    const progress = totalToImport > 0 ? processedCount / totalToImport : 0

    return (
      <div className="csv-import-step">
        <WizardModalProgressState>
          <p className="csv-import-status wizard-modal-label">
            {t('components.csv.import.importing')}
          </p>
          <WizardModalProgressBar value={Math.round(progress * 100)} />
          <p className="wizard-modal-progress-count">
            {processedCount} / {totalToImport}
          </p>
          <p className="wizard-modal-progress-note">
            {t('components.csv.import.keepOpen')}
          </p>
        </WizardModalProgressState>
      </div>
    )
  }

  return (
    <div className="csv-import-step">
      <div className="csv-import-done">
        <div className="csv-import-done-icon">
          <Icon icon="check" size="lg" />
        </div>
        <p className="csv-import-done-title wizard-modal-title">
          {t('components.csv.import.complete')}
        </p>
        <div className="csv-import-summary-row wizard-modal-label">
          <span>{t('components.csv.import.created')}</span>
          <span className="csv-import-summary-value csv-import-summary-value--positive">
            +{createdCount}
          </span>
        </div>
        {updatedCount > 0 && (
          <div className="csv-import-summary-row wizard-modal-label">
            <span>{t('components.csv.import.updated')}</span>
            <span className="csv-import-summary-value csv-import-summary-value--positive">
              +{updatedCount}
            </span>
          </div>
        )}
        {failedCount > 0 && (
          <div className="csv-import-summary-row wizard-modal-label">
            <span>{t('components.csv.import.failed')}</span>
            <span className="csv-import-summary-value csv-import-summary-value--negative">
              -{failedCount}
            </span>
          </div>
        )}
        {failedImportRowValues.length > 0 && (
          <button
            type="button"
            className="csv-analysis-card-download wizard-modal-meta"
            onClick={downloadFailedRows}
          >
            <Icon icon="download-circle" size="sm" />
            <span>{t('components.csv.import.downloadFailed')}</span>
          </button>
        )}
        {importDuration !== null && (
          <p className="csv-import-duration wizard-modal-meta">
            <Icon icon="clock" size="sm" />
            {t('components.csv.import.duration')}{' '}
            {formatDuration(importDuration)}
          </p>
        )}
      </div>
    </div>
  )
}
